/* eslint-disable react/no-unescaped-entities */
/**
 * Generic passcode login for HWYCF and She Is (multi-program).
 * Renders based on the `program_slug` prop — stores the token under
 * the program-specific localStorage key.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PROG = {
  "healing-what-you-cannot-feel": {
    title: "Healing What You Cannot Feel",
    eyebrow: "Program 02 · Emotional Healing",
    tokenKey: "hwycf_token", emailKey: "hwycf_email", nameKey: "hwycf_name",
    appPath: "/coaching/healing-what-you-cannot-feel/app",
    salesPath: "/coaching/healing-what-you-cannot-feel",
  },
  "she-is": {
    title: "She Is",
    eyebrow: "Program 03 · Arrival & Embodiment",
    tokenKey: "she_is_token", emailKey: "she_is_email", nameKey: "she_is_name",
    appPath: "/coaching/she-is/app",
    salesPath: "/coaching/she-is",
  },
};

export default function CoachingLinearLogin({ programSlug }) {
  const cfg = PROG[programSlug];
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [passcode, setPasscode] = useState("");
  const [step, setStep] = useState("email");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [devCode, setDevCode] = useState("");

  async function reqPasscode(e) {
    e.preventDefault(); setLoading(true); setError("");
    try {
      const r = await fetch(`${API}/api/auth/coaching-client/multi/request-passcode`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), program_slug: programSlug }),
      });
      const b = await r.json();
      if (b.passcode_dev) setDevCode(b.passcode_dev);
      setStep("verify");
    } catch { setError("Could not send passcode. Please try again."); }
    setLoading(false);
  }
  async function verify(e) {
    e.preventDefault(); setLoading(true); setError("");
    try {
      const r = await fetch(`${API}/api/auth/coaching-client/multi/verify-passcode`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(), passcode: passcode.trim(),
          program_slug: programSlug,
        }),
      });
      if (!r.ok) { setError("Invalid or expired passcode."); setLoading(false); return; }
      const b = await r.json();
      localStorage.setItem(cfg.tokenKey, b.token);
      localStorage.setItem(cfg.emailKey, b.email);
      if (b.name) localStorage.setItem(cfg.nameKey, b.name);
      navigate(cfg.appPath);
    } catch { setError("Could not verify passcode."); }
    setLoading(false);
  }

  return (
    <div data-testid={`coaching-login-${programSlug}`}
         className="min-h-screen flex items-center justify-center px-6"
         style={{ background: "#141014", fontFamily: "'Inter', sans-serif" }}>
      <div className="w-full max-w-md rounded-2xl p-8"
           style={{ background: "#1E1922", border: "1px solid rgba(192,192,192,0.14)",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.45)" }}>
        <div className="text-[10px] uppercase tracking-[0.24em]"
             style={{ color: "#9B59D9", fontWeight: 700 }}>
          {cfg.eyebrow}
        </div>
        <h1 className="mt-2 text-3xl" style={{ fontFamily: "'Playfair Display', serif", color: "#F2EEF6" }}>
          Sign in to {cfg.title}
        </h1>
        <p className="mt-2 text-sm" style={{ color: "#B7ADC2" }}>
          {step === "email"
            ? "Enter the email you enrolled with. We'll send you a 6-digit passcode."
            : `Enter the 6-digit passcode we just sent to ${email}.`}
        </p>

        {step === "email" ? (
          <form onSubmit={reqPasscode} className="mt-6 space-y-4">
            <input type="email" required value={email}
                   onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
                   className="w-full rounded-xl px-4 py-3 text-sm focus:outline-none"
                   style={{ background: "#262029", color: "#F2EEF6",
                            border: "1px solid rgba(192,192,192,0.14)" }}
                   data-testid="login-email-input" />
            <button type="submit" disabled={loading || !email} data-testid="login-request-btn"
                    className="w-full rounded-full px-6 py-3 text-sm font-medium text-white disabled:opacity-40 inline-flex items-center justify-center gap-2"
                    style={{ background: "linear-gradient(135deg,#5B2C83,#9B59D9)",
                             boxShadow: "0 0 24px rgba(155,89,217,0.4)" }}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> :
               <>Send my passcode <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        ) : (
          <form onSubmit={verify} className="mt-6 space-y-4">
            {devCode && (
              <div className="text-xs rounded-xl p-3"
                   style={{ background: "rgba(155,89,217,0.15)", color: "#9B59D9" }}>
                <strong>Dev mode</strong> · your passcode is <code>{devCode}</code>
              </div>
            )}
            <input inputMode="numeric" maxLength={6} required value={passcode}
                   onChange={(e) => setPasscode(e.target.value.replace(/\D/g, ""))}
                   placeholder="6-digit passcode"
                   className="w-full rounded-xl px-4 py-3 text-lg text-center tracking-widest focus:outline-none"
                   style={{ background: "#262029", color: "#F2EEF6",
                            border: "1px solid rgba(192,192,192,0.14)" }}
                   data-testid="login-passcode-input" />
            <button type="submit" disabled={loading || passcode.length !== 6}
                    data-testid="login-verify-btn"
                    className="w-full rounded-full px-6 py-3 text-sm font-medium text-white disabled:opacity-40 inline-flex items-center justify-center gap-2"
                    style={{ background: "linear-gradient(135deg,#5B2C83,#9B59D9)",
                             boxShadow: "0 0 24px rgba(155,89,217,0.4)" }}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> :
               <>Verify &amp; enter <ArrowRight className="w-4 h-4" /></>}
            </button>
            <button type="button" onClick={() => { setStep("email"); setPasscode(""); setDevCode(""); }}
                    className="text-xs" style={{ color: "#7A7284" }} data-testid="login-change-email-btn">
              Use a different email
            </button>
          </form>
        )}

        {error && <div className="mt-4 text-sm" style={{ color: "#e97070" }} data-testid="login-error">{error}</div>}

        <div className="mt-8 text-xs" style={{ color: "#7A7284" }}>
          Not enrolled yet? <Link to={cfg.salesPath} className="underline" style={{ color: "#9B59D9" }}>Learn about {cfg.title}</Link>
        </div>
      </div>
    </div>
  );
}
